/**
 * Resolve the prefix (prename) text for a person.
 *
 * The full and short forms fall back to each other symmetrically: whichever
 * form is requested is preferred, but if it's blank the other form is used
 * instead. '' is only returned when both forms are blank.
 *
 * @param  {Object}  prefix           The person's prefix object, may be null.
 * @param  {Object}  options
 * @param  {Boolean} options.short    Prefer the short form.
 * @param  {Boolean} options.eng      Use the Eng fields instead of Local.
 * @return {String}  The prefix text, or '' when unavailable.
 */
const prefixText = (prefix, { short, eng }) => {
  if (!prefix) return '';

  const full = eng ? prefix.nameEng : prefix.nameLocal;
  const abbreviated = eng ? prefix.nameShortEng : prefix.nameShortLocal;

  return (short ? abbreviated || full : full || abbreviated) || '';
};

/**
 * Format a person's (teacher or student) display name.
 *
 * Never reads the active locale: the caller picks the field set via `eng`,
 * which keeps this a pure function of its arguments.
 *
 * @param  {Object}  person           Teacher/student record, may be null.
 * @param  {Object}  [options]
 * @param  {Boolean} [options.short]  Use the short prefix form.
 * @param  {Boolean} [options.eng]    Use the Eng field set instead of Local.
 * @return {String}  e.g. "ผู้ช่วยศาสตราจารย์สมชาย ใจดี". '' when unrenderable.
 */
export const formatPersonName = (person, { short = false, eng = false } = {}) => {
  if (!person) return '';

  const firstName = eng ? person.firstNameEng : person.firstNameLocal;
  const lastName = eng ? person.lastNameEng : person.lastNameLocal;

  const name = [firstName, lastName].filter(Boolean).join(' ');
  if (!name) return '';

  return [prefixText(person.prefix, { short, eng }), name]
    .filter(Boolean)
    .join(eng ? ' ' : '');
};
